import type { ApiComponents } from '@aztec-realms/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import type { PlayerVerticalSliceView } from '@aztec-realms/shared-types';

import { browserApiClient, requiredApiData } from './browser-api';
import { commandId, verticalSliceQueryKey } from './use-vertical-slice';
import {
  decodeDefinition,
  openDailyDecodeOnEntry,
  selectPersonalRestorationSession,
} from './api-vertical-slice';

export type PersonalRestoration = ApiComponents['schemas']['PersonalRestorationResponse'];
export type PersonalGoal = NonNullable<PersonalRestoration['goal']>;
export type DailyProgress = ApiComponents['schemas']['DailyProgressResponse'];
export type PlayerMissions = ApiComponents['schemas']['MissionListResponse'];
export const personalProgressKey = ['player', 'personal-progress'] as const;
export const restorationKey = (owner: string) => [...personalProgressKey, owner, 'restoration'];
export const dailyProgressKey = (owner: string) => [...personalProgressKey, owner, 'daily'];
export const playerMissionsKey = (owner: string) => [...personalProgressKey, owner, 'missions'];

export function usePlayerMissions(owner: string, enabled: boolean) {
  return useQuery({
    queryKey: playerMissionsKey(owner),
    queryFn: async ({ signal }) => {
      const response = await browserApiClient().GET('/api/v1/missions', { signal });
      return requiredApiData(response.data, response.error, 'Your goals could not be loaded.');
    },
    enabled: enabled && Boolean(owner),
    retry: false,
    staleTime: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

/** Explicit Daily entry reuses the guarded source command and never replaces unfinished work. */
export function useMissionDaily(owner: string, locked: boolean) {
  const cache = useQueryClient();
  const attemptKey = useAttemptKeys();
  const latest = useRef({ owner, locked, mounted: true });
  useEffect(() => {
    latest.current = { owner, locked, mounted: true };
    return () => {
      latest.current.mounted = false;
    };
  }, [owner, locked]);
  return useMutation({
    retry: false,
    networkMode: 'always',
    mutationFn: async () => {
      assertInteractive(locked);
      const current = cache.getQueryData<PlayerVerticalSliceView>(verticalSliceQueryKey);
      if (!current || current.profile.userId !== owner)
        throw new Error('Your realm must finish loading before you can continue.');
      if (current.decode.status !== 'solved') return { kind: 'resume' as const };
      const canContinue = () => {
        const now = cache.getQueryData<PlayerVerticalSliceView>(verticalSliceQueryKey);
        return (
          latest.current.mounted &&
          latest.current.owner === owner &&
          !latest.current.locked &&
          navigator.onLine &&
          now?.profile.userId === owner &&
          now.decode.runId === current.decode.runId &&
          now.decode.status === 'solved'
        );
      };
      const result = await openDailyDecodeOnEntry({
        entryRunId: current.decode.runId,
        commandId: attemptKey.get(`${owner}:${current.decode.runId}`),
        canContinue,
        client: browserApiClient({ mutation: true }),
      });
      if (!canContinue() || result.kind === 'stale')
        throw new Error('Your Daily check was interrupted. Open your goals to try again.');
      attemptKey.clear();
      if (result.kind === 'opened' || result.kind === 'resume')
        cache.setQueryData<PlayerVerticalSliceView>(verticalSliceQueryKey, (previous) =>
          previous?.profile.userId === owner && previous.decode.runId === current.decode.runId
            ? { ...previous, decode: result.decode }
            : previous,
        );
      return result;
    },
  });
}

export function usePersonalRestoration(owner: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: restorationKey(owner ?? 'signed-out'),
    queryFn: async ({ signal }) => {
      const response = await browserApiClient().GET('/api/v1/personal-restoration', { signal });
      return requiredApiData(
        response.data,
        response.error,
        'Your personal goal could not be loaded.',
      );
    },
    enabled: enabled && Boolean(owner),
    retry: false,
    staleTime: 0,
  });
}

export function useDailyProgress(owner: string, enabled: boolean) {
  return useQuery({
    queryKey: dailyProgressKey(owner),
    queryFn: async ({ signal }) => {
      const response = await browserApiClient().GET('/api/v1/daily-progress', { signal });
      return requiredApiData(
        response.data,
        response.error,
        'Your Daily progress could not be loaded.',
      );
    },
    enabled,
    retry: false,
    staleTime: 0,
  });
}

/** Explicit retries retain the same operation key; no offline mutation queue. */
function useAttemptKeys() {
  const keys = useRef(new Map<string, string>());
  return {
    get: (payload: string) => {
      const key = keys.current.get(payload) ?? commandId('personal.progress');
      keys.current.set(payload, key);
      return key;
    },
    clear: () => keys.current.clear(),
  };
}

function assertInteractive(locked: boolean) {
  if (locked || !navigator.onLine) throw new Error('Stop safely and reconnect before continuing.');
}

export function useSavePersonalGoal(owner: string, locked: boolean) {
  const cache = useQueryClient();
  const attemptKey = useAttemptKeys();
  return useMutation({
    retry: false,
    networkMode: 'always',
    mutationFn: async (body: { building_code: string; building_id?: string }) => {
      assertInteractive(locked);
      const response = await browserApiClient({ mutation: true }).POST(
        '/api/v1/personal-restoration/goal',
        {
          body,
          params: { header: { 'Idempotency-Key': attemptKey.get(JSON.stringify(body)) } },
        },
      );
      return requiredApiData(
        response.data,
        response.error,
        'Your goal could not be saved. Try again.',
      );
    },
    onSuccess: (data) => {
      attemptKey.clear();
      cache.setQueryData(restorationKey(owner), data);
    },
  });
}

export function useRepairDaily(owner: string, locked: boolean) {
  const cache = useQueryClient();
  const attemptKey = useAttemptKeys();
  return useMutation({
    retry: false,
    networkMode: 'always',
    mutationFn: async (day: string) => {
      assertInteractive(locked);
      const response = await browserApiClient({ mutation: true }).POST(
        '/api/v1/daily-progress/repairs',
        {
          body: { day },
          params: { header: { 'Idempotency-Key': attemptKey.get(day) } },
        },
      );
      return requiredApiData(
        response.data,
        response.error,
        'This day could not be repaired. Try again.',
      );
    },
    onSuccess: (data) => {
      cache.setQueryData(dailyProgressKey(owner), data.progress);
    },
    onError: () => {
      void cache.invalidateQueries({ queryKey: dailyProgressKey(owner) });
    },
  });
}

export function usePersonalEncounter(owner: string, locked: boolean) {
  const cache = useQueryClient();
  const attemptKey = useAttemptKeys();
  return useMutation({
    retry: false,
    networkMode: 'always',
    mutationFn: async (code: string) => {
      assertInteractive(locked);
      if (!['garden_channels', 'garden_crossings', 'garden_resonance'].includes(code))
        throw new Error('This garden chapter is not available. Refresh your choices.');
      const response = await browserApiClient({ mutation: true }).POST(
        '/api/v1/personal-restoration/encounters',
        {
          body: {
            encounter_code: code as 'garden_channels' | 'garden_crossings' | 'garden_resonance',
          },
          params: { header: { 'Idempotency-Key': attemptKey.get(code) } },
        },
      );
      return requiredApiData(
        response.data,
        response.error,
        'This garden glyph could not open. Try again.',
      );
    },
    onSuccess: async (session) => {
      attemptKey.clear();
      const current = cache.getQueryData<PlayerVerticalSliceView>(verticalSliceQueryKey);
      if (current?.profile.userId !== owner) return;
      selectPersonalRestorationSession(session.id);
      cache.setQueryData(verticalSliceQueryKey, { ...current, decode: decodeDefinition(session) });
      await cache.invalidateQueries({ queryKey: restorationKey(owner) });
    },
  });
}

export function useResumePersonalEncounter(owner: string, locked: boolean) {
  const cache = useQueryClient();
  return useMutation({
    retry: false,
    networkMode: 'always',
    mutationFn: async (sessionId: string) => {
      assertInteractive(locked);
      const response = await browserApiClient().GET('/api/v1/decode/sessions/{session_id}', {
        params: { path: { session_id: sessionId } },
      });
      const session = requiredApiData(
        response.data,
        response.error,
        'Your garden glyph could not be resumed.',
      );
      if (session.reward_mode !== 'personal_materials')
        throw new Error('This garden glyph is no longer available.');
      return session;
    },
    onSuccess: (session) => {
      const current = cache.getQueryData<PlayerVerticalSliceView>(verticalSliceQueryKey);
      if (current?.profile.userId !== owner) return;
      selectPersonalRestorationSession(session.id);
      cache.setQueryData(verticalSliceQueryKey, { ...current, decode: decodeDefinition(session) });
    },
  });
}

export function personalGoalLabel(goal: PersonalGoal): string {
  return goal.building_code
    .split('_')
    .map((word) => word[0].toUpperCase() + word.slice(1))
    .join(' ');
}

export function personalGoalPath(goal: PersonalGoal): string {
  return goal.return_action.building_id
    ? `/home?panel=selected&building=${encodeURIComponent(goal.return_action.building_id)}`
    : `/home?panel=build&goal=${encodeURIComponent(goal.building_code)}`;
}

export function personalGoalSummary(goal: PersonalGoal): string {
  if (goal.status === 'completed') return 'Restored in your city. Choose what grows next.';
  if (goal.status === 'waiting') return 'Growing in your city. Play while it takes shape.';
  if (goal.status === 'paused') return 'Review your goal to choose the next step.';
  const deficits = Object.entries(goal.deficits).filter(([, value]) => value > 0);
  return deficits.length
    ? `Still needed: ${deficits.map(([code, amount]) => `${amount} ${code}`).join(', ')}.`
    : 'Your materials are ready. Review construction.';
}
